// client/src/components/pages/OperationOverview/overviewKpis.ts (converted from .js, ST-00216)
//
// The Operations Overview headline tiles (Routes, Dispatched, Received, Total
// Loads, Avg Variance) and the PDF export's KPI row (ST-00215, RSO-3 / RSO-11).
//
// The headline must agree with the route cards underneath it. The server sends
// `deduplicatedTotals` computed over exactly the routes shown as cards (each
// with its own BF/CF match mode and rules, a ticket on two routes counted
// once) and lists them in `deduplicatedTotals.groupingIds`. The tiles use those
// figures only when that list is the set of cards on screen; otherwise — a
// search narrowed the cards, or an older server/cached response without the
// list — they are the sum of the visible cards, so the tiles always describe
// what the page shows.

//
// ST-00218 P3: the server sends each card's (and the headline's) adjusted dispatch — on a
// "Truck + time" route also less received off-route and no receipt data — and its variance.
// They are used when present; an older or cached response falls back to dispatched + BF − CF.
//
// ST-00298: a variance needs an adjusted dispatch over 0.5 t (utils/variance). A route whose
// receipts stand against nothing left dispatched (every dispatch carried forward) reads n/a, and
// Avg variance leaves it out: its tonnes would read as an over-delivery.

import type {
  DeduplicatedTotals,
  FallbackNote,
  OverviewGrouping,
  OverviewKpis,
  RouteTotalsFigures,
} from '../../../types/clientPages/operationOverview';
import type { DataStatus } from '../../../types/loadMatching';
import type { MatchingInfo } from '../../../types/routeFigures';
import { lacksVarianceBase, NO_VARIANCE_BASE_TEXT, varianceOf } from '../../../utils/variance';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

// Adjusted dispatch for one route card (matches Route Summary): the server's figure, else
// dispatched + BF − CF.
export const adjustedDispatch = (g: RouteTotalsFigures): number =>
  isNum(g.adjustedDispatchWeight)
    ? g.adjustedDispatchWeight
    : (g.totalDispatchWeight || 0) + (g.bfWeight || 0) - (g.cfWeight || 0);

export const adjustedDispatchCount = (g: RouteTotalsFigures): number =>
  isNum(g.adjustedDispatchCount)
    ? g.adjustedDispatchCount
    : (g.totalDispatchCount || 0) + (g.bfCount || 0) - (g.cfCount || 0);

/**
 * The variance is "n/a": the server sent none (`variancePct: null`) because the route has no
 * (or only partial) receipt data (`dataStatus` set and not 'ok'). A null variance with an ok or
 * absent status (nothing dispatched) is 0 % when nothing was received either, and n/a when
 * receipts stand against nothing left dispatched (lacksDispatchBase, ST-00298).
 */
export const isVarianceNa = (g: Pick<RouteTotalsFigures, 'variancePct' | 'dataStatus'>): boolean =>
  g.variancePct === null && !!g.dataStatus && g.dataStatus !== 'ok';

/**
 * ST-00298: the route received (> 0.5 t) but nothing is left dispatched to compare with (adjusted
 * dispatch ≤ 0.5 t, e.g. every dispatch carried forward): no variance, whatever the server sent.
 */
export const lacksDispatchBase = (g: RouteTotalsFigures): boolean =>
  lacksVarianceBase(g.totalReceiptWeight || 0, adjustedDispatch(g));

/** Why a route has no variance (its data status), in words. */
const NA_TEXT: Partial<Record<DataStatus, string>> = {
  no_receipt_data: 'No receipt data',
  partial_receipt_data: 'No receipt data for part of the period',
  no_dispatch_data: 'No dispatch data',
  empty: 'No data',
};

/**
 * Why a variance is n/a, in words: the route's data status, else (ST-00298) nothing left
 * dispatched when receipts stand against no adjusted dispatch. Every page's n/a tooltip.
 */
export const varianceNaReason = (
  dataStatus: DataStatus | null | undefined,
  receivedT: number,
  adjustedT: number
): string =>
  (dataStatus && dataStatus !== 'ok' && NA_TEXT[dataStatus]) ||
  (lacksVarianceBase(receivedT, adjustedT) ? NO_VARIANCE_BASE_TEXT : 'No receipt data');

/** varianceNaReason of a route card. */
export const naReasonOf = (g: RouteTotalsFigures): string =>
  varianceNaReason(g.dataStatus, g.totalReceiptWeight || 0, adjustedDispatch(g));

/**
 * The fallback note to show once on the page: the first route (or the headline) whose
 * "Truck + time" matching fell back to delivery-number matching. null when none did.
 */
export const fallbackNoteOf = (
  items: ReadonlyArray<{ matching?: MatchingInfo | null } | null | undefined> | null | undefined,
  headline?: { matching?: MatchingInfo | null } | null
): FallbackNote | null => {
  for (const item of [...(items || []), headline]) {
    const m = item?.matching;
    if (m && m.fallback && m.note) return { note: m.note, detail: m.detail ?? null };
  }
  return null;
};

// A route shows as a card when it is in reporting and both dispatched and
// received something in the period (same rule as the server's headline).
export const isShownAsCard = (
  g: Pick<OverviewGrouping, 'totalDispatchWeight' | 'totalReceiptWeight' | 'includeInReporting'>
): boolean =>
  g.totalDispatchWeight > 0 && g.totalReceiptWeight > 0 && g.includeInReporting !== false;

export const filterRouteCards = <G extends OverviewGrouping>(
  groupings: G[] | null | undefined,
  searchTerm: string | null = ''
): G[] => {
  const term = (searchTerm || '').toLowerCase();
  return (groupings || []).filter(
    (g) => isShownAsCard(g) && (g.name || '').toLowerCase().includes(term)
  );
};

const sameIdSet = (a: unknown, b: unknown): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const set = new Set(a.map(Number));
  return b.every((id) => set.has(Number(id)));
};

/**
 * @param cards - the route cards on screen (filterRouteCards output)
 * @param deduplicatedTotals - from GET /route-summary/groupings
 * @returns the tiles; `source`: 'server' (overlap-safe headline for exactly
 *   these cards) or 'cards' (sum of the cards shown).
 */
export const computeOverviewKpis = (
  cards: OverviewGrouping[] | null | undefined,
  deduplicatedTotals: DeduplicatedTotals | null | undefined
): OverviewKpis => {
  const list = cards || [];
  // Volume-weighted variance of the period: (received - dispatched) / dispatched, null = n/a.
  // ST-00298: without the routes that lack a dispatch base — their tonnes stay in the
  // Dispatched / Received tiles, but come out of the variance whole (a ticket they share with
  // another card is not known here, so it comes out too); n/a when that leaves no route.
  const noBase = list.filter(lacksDispatchBase);
  const outD = noBase.reduce((sum, g) => sum + adjustedDispatch(g), 0);
  const outR = noBase.reduce((sum, g) => sum + (g.totalReceiptWeight || 0), 0);
  const avgVarianceOf = (dispatched: number, received: number): number | null =>
    noBase.length > 0 && noBase.length === list.length
      ? null
      : varianceOf(received - outR, dispatched - outD);
  const excluded = noBase.length ? { varianceExcludedRoutes: noBase.length } : {};
  if (
    deduplicatedTotals &&
    sameIdSet(
      deduplicatedTotals.groupingIds,
      list.map((g) => g.id)
    )
  ) {
    const t = deduplicatedTotals;
    const dispatched = adjustedDispatch(t);
    const received = t.totalReceiptWeight || 0;
    return {
      totalRoutes: list.length,
      totalDispatched: dispatched,
      totalReceived: received,
      totalLoads: adjustedDispatchCount(t) + (t.totalReceiptCount || 0),
      avgVariance: avgVarianceOf(dispatched, received),
      ...excluded,
      source: 'server',
    };
  }

  const dispatched = list.reduce((sum, g) => sum + adjustedDispatch(g), 0);
  const received = list.reduce((sum, g) => sum + (g.totalReceiptWeight || 0), 0);
  return {
    totalRoutes: list.length,
    totalDispatched: dispatched,
    totalReceived: received,
    totalLoads: list.reduce(
      (sum, g) => sum + adjustedDispatchCount(g) + (g.totalReceiptCount || 0),
      0
    ),
    avgVariance: avgVarianceOf(dispatched, received),
    ...excluded,
    source: 'cards',
  };
};
